import { CookieSerializeOptions } from 'cookie'
import { getCookies } from 'cookies-next'
import { IncomingMessage, ServerResponse } from 'http'
import { CompactEncrypt, compactDecrypt, CompactDecryptResult } from 'jose'
import { deriveKey } from '@/auth/session/KeyGenerator'
import { logger } from '@/utils/logging'
import ClientConfig from '../ClientConfig'
import { CookieUtil } from './CookieUtil'
import { Session } from './Session'

const epoch = () => (Date.now() / 1000) | 0

const cookieUtil = new CookieUtil()
const alg = 'dir'
const enc = 'A256GCM'

export class CookieStore {
  config: ClientConfig
  key: Uint8Array

  constructor(config: ClientConfig) {
    this.config = config
    this.key = new Uint8Array(deriveKey(config.secret))
  }

  async read(req: IncomingMessage): Promise<{ json?: Record<string, unknown>; iat?: number }> {
    const cookies = getCookies({ req })
    const sessionName = this.config.session.name

    try {
      const cookieValue = cookies[sessionName]
      if (cookieValue) {
        const { plaintext, protectedHeader } = await this.decrypt(cookieValue)
        const iat = typeof protectedHeader.iat === 'number' ? protectedHeader.iat : undefined
        return { json: JSON.parse(new TextDecoder().decode(plaintext)), iat }
      }
    } catch (err) {
      logger().error('Error reading session cookie', { sessionName }, err)
    }

    return {}
  }

  forgetUser(req: IncomingMessage, res: ServerResponse) {
    const cookies = getCookies({ req })
    Object.keys(cookies).forEach((cookieName) => {
      cookieUtil.clear(cookieName, res)
    })
  }

  async save(
    req: IncomingMessage,
    res: ServerResponse,
    session: Session | undefined | null,
    createdAt?: number,
  ): Promise<void> {
    const { name: sessionName, ...sessionConfig } = this.config.session
    const { transient, ...cookieConfig } = sessionConfig.cookie

    if (!session) {
      cookieUtil.clear(sessionName, res)
      return
    }

    const uat = epoch()
    const iat = typeof createdAt === 'number' ? createdAt : uat
    const exp = this.calculateExp(iat, uat)
    const cookieOptions = { ...cookieConfig } as CookieSerializeOptions

    if (!transient) {
      cookieOptions.expires = new Date(exp * 1000)
    }

    const value = await this.encrypt(JSON.stringify(session), { iat, uat, exp })
    cookieUtil.save(sessionName, res, { value })
  }

  private calculateExp(iat: number, uat: number) {
    const { absoluteDuration, rollingDuration } = this.config.session

    return Math.min(uat + rollingDuration, iat + absoluteDuration)
  }

  private async decrypt(jwe: string): Promise<CompactDecryptResult> {
    return await compactDecrypt(jwe, this.key)
  }

  private async encrypt(payload: string, headers?: { iat: number; uat: number; exp: number }): Promise<string> {
    return await new CompactEncrypt(new TextEncoder().encode(payload))
      .setProtectedHeader({
        alg,
        enc,
        ...headers,
      })
      .encrypt(this.key)
  }
}
